import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ShieldCheck, Eye, EyeSlash, Envelope, Lock } from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [store, setStore] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/api/s/magere-viandes`)
      .then(r => setStore(r.data))
      .catch(() => {});
  }, []);

  const logoUrl = store?.identity?.logo_light || store?.identity?.logo_url;
  const colors = {
    primary: store?.identity?.primary_color || '#c2185b'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        if (result.user?.role === 'merchant' || result.user?.role === 'admin') {
          navigate('/admin');
        } else {
          setError('Accès refusé. Vous devez être administrateur.');
        }
      } else {
        setError(result.error || 'Identifiants incorrects');
      }
    } catch (err) {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{ 
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)',
        fontFamily: "'Jost', sans-serif"
      }}
    >
      <link href="https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;900&display=swap" rel="stylesheet" />
      
      <div className="w-full max-w-md">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          {logoUrl ? (
            <img src={logoUrl} alt="MAGERE" className="h-[100px] mx-auto mb-4 object-contain" />
          ) : (
            <div 
              className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-4 shadow-xl"
              style={{ backgroundColor: colors.primary }}
            >
              <ShieldCheck size={40} weight="fill" className="text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-white">Administration</h1>
          <p className="text-white/60 mt-1">MAGERE Viandes - Back-office</p>
        </div>

        {/* Login Form */}
        <form 
          onSubmit={handleSubmit}
          className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-2xl"
          data-testid="admin-login-form"
        >
          {error && (
            <div 
              className="mb-6 p-4 rounded-xl text-sm font-medium"
              style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', color: '#ef4444', border: '1px solid rgba(220, 38, 38, 0.2)' }}
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          {/* Email */}
          <div className="mb-5">
            <label className="block text-white/70 text-sm font-medium mb-2">
              Adresse email
            </label>
            <div className="relative">
              <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contact@magere-viandes.fr"
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3.5 pl-12 text-white placeholder:text-white/30 focus:outline-none focus:border-[#c2185b] focus:ring-2 focus:ring-[#c2185b]/20 transition-all"
                required
                data-testid="admin-email-input"
              />
            </div>
          </div>

          {/* Password */}
          <div className="mb-6">
            <label className="block text-white/70 text-sm font-medium mb-2">
              Mot de passe
            </label>
            <div className="relative">
              <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3.5 pl-12 pr-12 text-white placeholder:text-white/30 focus:outline-none focus:border-[#c2185b] focus:ring-2 focus:ring-[#c2185b]/20 transition-all"
                required
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ backgroundColor: colors.primary }}
            data-testid="admin-login-submit"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <ShieldCheck size={20} weight="fill" />
                Se connecter
              </>
            )}
          </button>

          {/* Back to site */}
          <div className="mt-6 text-center">
            <a 
              href="/"
              className="text-white/50 hover:text-white text-sm transition-colors"
            >
              ← Retour au site
            </a>
          </div>
        </form>

        {/* Footer */}
        <p className="text-center text-white/30 text-xs mt-8">
          MAGERE Viandes © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
